import Color from 'color'
import { Element, ElementContent, Properties, Text } from 'hast'
import { Root } from 'mdast'
import { Plugin } from 'unified'
import { remove } from 'unist-util-remove'
import { visit } from 'unist-util-visit'

import type { Node, Parent } from 'unist'

/** CJK考慮対象のMarkdown記号定義（） */
export const CJK_TARGET_MARKDOWN_SYMBOLS = ['[', '~', '*']

/** CJK考慮対象の記号定義 */
export const CJK_TARGET_NORMAL_SYMBOLS = [
  '（',
  '）',
  '(',
  ')',
  '［',
  '］',
  ']',
  '｛',
  '｝',
  '{',
  '}',
  '「',
  '」',
  '『',
  '』',
  '【',
  '】',
  '《',
  '》',
  '〈',
  '〉',
  '＂',
  '"',
  '＇',
  '\u201c',
  '\u201d',
  '\u2018',
  '\u2019',
  '，',
  ',',
  '．',
  '.',
  '！',
  '!',
  '？',
  '?',
  '：',
  ':',
  '；',
  ';',
  '－',
  '-',
  '〜',
  '／',
  '/',
  '＼',
  '｜',
  '|',
  '＊',
  '＋',
  '+',
  '＝',
  '=',
  '＜',
  '<',
  '＞',
  '>',
  '＠',
  '@',
  '＃',
  '#',
  '＆',
  '&',
  '％',
  '%',
  '＄',
  '$',
  '☆',
  '★',
  '○',
  '●',
  '◎',
  '◇',
  '◆',
  '□',
  '■',
  '△',
  '▲',
  '▽',
  '▼',
  '※',
  '─',
  '│',
  '┌',
  '┐',
  '└',
  '┘',
  '├',
  '┤',
  '┬',
  '┴',
  '┼',
  '━',
  '┃',
  '┏',
  '┓',
  '┗',
  '┛',
  '┣',
  '┫',
  '┳',
  '┻',
  '╋',
]

const CJK_TARGET_SYMBOL = [...CJK_TARGET_MARKDOWN_SYMBOLS, ...CJK_TARGET_NORMAL_SYMBOLS, ' ', '\\']

/**非サポートのbタグをサポート対象のstrongタグに変換する */
export const rehypeConvertFromBTagToStrongTag: Plugin = () => {
  return tree => {
    visit(tree, 'element', (node: Element) => {
      if (node.tagName === 'b') {
        node.tagName = 'strong'
      }
    })
  }
}

/** 非サポートのiタグをサポート対象のemタグに変換する */
export const rehypeConvertFromITagToEmTag: Plugin = () => {
  return tree => {
    visit(tree, 'element', (node: Element) => {
      if (node.tagName === 'i') {
        node.tagName = 'em'
      }
    })
  }
}

/** aタグのマークアップを `アンカーテキスト[URL]` に変換する */
export const rehypeConvertFromATagToText: Plugin = () => {
  return tree => {
    visit(tree, 'element', (node: Element, index, parent: Element) => {
      if (node.tagName === 'a' && node.properties && node.properties['href']) {
        const url = node.properties['href']
        const text = node.children
          .map((child: ElementContent) => {
            if (child.type === 'text') {
              return (child as Text).value
            }
            return ''
          })
          .join('')
        parent.children[index] = {
          type: 'text',
          value: `${text}[${url}]`,
        }
      }
    })
  }
}

/**
 * <span style="color: …"> を常に #rrggbb へ正規化する Rehype プラグイン
 *
 * Tiptap エディタがRGB表記を返す場合があるため、常に #rrggbb へ正規化する.
 * 解析できない値（色名など）はそのまま残す
 */
export const rehypeHexColor: Plugin = () => {
  return tree => {
    visit(tree, 'element', (node: Element) => {
      if (node.tagName === 'span' && node.properties && node.properties['style']) {
        const styleAttr: string = node.properties['style'] as string

        const styleMap: Record<string, string> = Object.fromEntries(
          styleAttr
            .split(';')
            .map(d => d.split(':').map(s => s.trim()))
            .filter(a => a.length === 2)
        )

        if (styleMap['color']) {
          try {
            styleMap['color'] = Color(styleMap['color']).hex().toLowerCase()
          } catch {
            /**  不正な値はスルーする. */
          }
        }

        ;(node.properties as Properties)['style'] = Object.entries(styleMap)
          .map(([k, v]) => `${k}: ${v}`)
          .join(';')
      }
    })
  }
}

/** HTMLのコメントを削除するRehype プラグイン.*/
export const remarkStripHtmlComments: Plugin = () => {
  return tree => {
    remove(tree, (node: Node) => {
      if (node.type === 'text') {
        return /^\s*<!--/.test((node as Text).value)
      }
      return false
    })
  }
}

/** attributes が存在しないspanタグを削除する */
export const rehypeRemoveNoAttributeSpanTag: Plugin = () => {
  return tree => {
    visit(tree, 'element', (node: Element, index, parent: Element) => {
      if (node.tagName === 'span' && Object.keys(node.properties).length === 0) {
        parent.children.splice(index, 1, ...node.children)
      }
    })
  }
}

/**
 * CJK環境下でのMarkdown表記の正規化を行うRehypeプラグイン
 *
 * 太字表記（**太字**）などの文字装飾の直後に対象の記号がある場合、
 * その直後にスペースを挿入することで、CJK環境下でのMarkdownレンダリングをサポートする..
 */
export const rehypeCJKMarkdownNormalization: Plugin<[], Root> = () => {
  /** CJK対象のHTMLタグ定義 */
  const CJK_TARGET_TAGS = ['b', 'strong']

  const startsWithTargetSymbol = (el: Element): boolean => {
    const firstTextNode = getFirstTextNode(el)
    return !!firstTextNode && CJK_TARGET_SYMBOL.some(sym => firstTextNode.value.startsWith(sym))
  }

  const endsWithTargetSymbol = (el: Element): boolean => {
    const lastTextNode = getLastTextNode(el)
    return !!lastTextNode && CJK_TARGET_SYMBOL.some(sym => lastTextNode.value.endsWith(sym))
  }

  const getFirstTextNode = (el: Element): Text | undefined => {
    for (let i = 0; i < el.children.length; i++) {
      const elc: ElementContent = el.children[i]
      if (elc.type === 'text') {
        return elc
      } else if (elc.type === 'element') {
        const textNode = getFirstTextNode(elc)
        if (textNode) return textNode
      }
    }
    return undefined
  }

  const getLastTextNode = (el: Element): Text | undefined => {
    for (let i = el.children.length - 1; i >= 0; i--) {
      const elc: ElementContent = el.children[i]
      if (elc.type === 'text') {
        return elc
      } else if (elc.type === 'element') {
        const textNode = getLastTextNode(elc)
        if (textNode) return textNode
      }
    }
    return undefined
  }

  return (tree: Root) => {
    visit(tree, 'element', (node: Element, index: number | null, parent: Parent | null) => {
      if (!parent || index === null) return
      if (!CJK_TARGET_TAGS.includes(node.tagName)) return

      /**  先頭が指定記号の際のNormalization */
      if (startsWithTargetSymbol(node)) {
        const prev: Node | undefined = parent.children[index - 1]
        if (prev && prev.type === 'text') {
          const prevText = prev as Text
          if (!/\s$/.test(prevText.value)) {
            prevText.value = prevText.value + ' '
          }
        }
      }

      /**  末尾が指定記号の際のNormalization */
      if (endsWithTargetSymbol(node)) {
        const next: Node | undefined = parent.children[index + 1]
        if (next && next.type === 'text') {
          const nextText = next as Text
          if (!/^\s/.test(nextText.value)) {
            nextText.value = ' ' + nextText.value
          }
        }
      }
    })
  }
}
